import { Visibility as VisibilityIcon } from '@mui/icons-material';
import { Box, IconButton, Stack, Tooltip } from '@mui/material';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { getModelLabel } from '../../utils/ModelIconMapping';
import { Palette } from 'Palette';
import { StyledCard, StyledTypography } from '../styles/styles';
import { getModelNavigationPath } from './contentVersionViewerUtils';

export interface ActivityLogModelCardProps {
  title: string;
  subtitle: string;
  icon: React.ReactElement;
  tone: keyof typeof Palette;
  modelType: string;
  modelId: string;
  maxWidth?: string;
}

const ActivityLogModelCard: React.FC<ActivityLogModelCardProps> = ({
  title,
  subtitle,
  icon,
  tone,
  modelType,
  modelId,
  maxWidth = '200px',
}) => {
  const navigate = useNavigate();

  const handleViewDetails = () => {
    navigate(getModelNavigationPath(modelType, modelId));
  };

  return (
    <Box
      sx={{
        position: 'relative',
        cursor: 'pointer',
        width: '100%',
        maxWidth,
        '&:hover .action-overlay': {
          opacity: 1,
          visibility: 'visible',
        },
      }}
    >
      <StyledCard
        tone={tone}
        sx={{
          padding: '0.75rem 1rem',
          marginBottom: '4px',
          width: '100%',
          minHeight: '2.5rem',
          borderRadius: '6px',
          '&:hover': {
            backgroundColor: Palette[tone].border,
            transform: 'scale(1.02)',
            transition: 'all 0.2s ease',
          },
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Box
            sx={{
              width: '20px',
              height: '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: Palette[tone].text,
              color: Palette[tone].background,
              borderRadius: '50%',
            }}
          >
            {React.cloneElement(icon, {
              sx: {
                fontSize: '0.8rem',
                width: '1rem',
                height: '1rem',
              },
            })}
          </Box>
          <StyledTypography
            tone={tone}
            sx={{
              fontSize: '0.75rem',
              fontWeight: 600,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              flex: 1,
            }}
          >
            {title}
          </StyledTypography>
        </Stack>
        <StyledTypography
          tone={tone}
          sx={{
            fontSize: '0.6rem',
            opacity: 0.7,
            marginLeft: '24px',
          }}
        >
          {subtitle}
        </StyledTypography>
      </StyledCard>
      <Box
        className="action-overlay"
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.85)',
          borderRadius: '4px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          opacity: 0,
          visibility: 'hidden',
          transition: 'opacity 0.2s ease',
          zIndex: 10,
          width: '100%',
          height: '100%',
          margin: 0,
          padding: 0,
        }}
      >
        <StyledTypography
          sx={{
            color: 'white',
            fontSize: '0.6rem',
            fontWeight: 700,
            marginBottom: '6px',
            textAlign: 'center',
            lineHeight: 1.1,
          }}
        >
          {title}
        </StyledTypography>
        <Stack direction="row" spacing={0.5}>
          <Tooltip title={`View ${getModelLabel(modelType)}`}>
            <IconButton
              onClick={handleViewDetails}
              sx={{
                width: '20px',
                height: '20px',
                backgroundColor: 'rgba(255,255,255,0.9)',
                color: 'rgba(0,0,0,0.8)',
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,1)',
                  color: 'rgba(0,0,0,1)',
                },
                '& .MuiSvgIcon-root': {
                  fontSize: '0.7rem',
                },
              }}
            >
              <VisibilityIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>
    </Box>
  );
};

export default ActivityLogModelCard;
